import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { useForm } from '@myphx-engineering/phx-design-system/hooks';

import { CONSTANTS } from '../constants';
import { getLongestCommonSubString } from '../utils/get-longest-substring';

export type FormWithPassword = {
    password: string;
    email?: string;
    phoneNumber?: string;
    enforcePasswordStrength?: boolean;
};

export const usePasswordValidation = <
    T extends FormWithPassword = FormWithPassword,
>(
    form: ReturnType<typeof useForm<T>>,
    enforcePasswordStrength = true
) => {
    const { t } = useTranslation();
    const { minimumPasswordLength } = CONSTANTS;

    const checkPassword = useCallback(
        (password: string, email?: string, phoneNumber?: string) => {
            if (!form.isTouched('password') || !enforcePasswordStrength) {
                return;
            }

            if (password.length < CONSTANTS.minimumPasswordLength) {
                form.setFieldError(
                    'password',
                    t('passwordHelpText.enterPasswordMinChar', {
                        minChar: minimumPasswordLength,
                    })
                );
                return;
            }

            if (/\s/.test(password)) {
                form.setFieldError(
                    'password',
                    t('passwordHelpText.containSpaces')
                );
                return;
            }

            const isSimilarToEmail = email
                ? getLongestCommonSubString(email, password) >= 4
                : false;

            const isSimilarToPhoneNumber = phoneNumber
                ? getLongestCommonSubString(
                      phoneNumber.replace(/[^\d+]+/g, '').trim(),
                      password
                  ) >= 4
                : false;

            if (isSimilarToEmail || isSimilarToPhoneNumber) {
                form.setFieldError(
                    'password',
                    t('passwordHelpText.similarToEmailOrMobile')
                );
                return;
            }

            form.clearFieldError('password');
        },
        [form, t, minimumPasswordLength, enforcePasswordStrength]
    );

    useEffect(() => {
        const { email, password, phoneNumber } = form.values;
        checkPassword(password, email, phoneNumber);
    }, [
        form.values.email,
        form.values.password,
        form.values.phoneNumber,
        checkPassword,
    ]);

    return { checkPassword };
};
